import React, { useState } from 'react';
import { Article } from '../types/api.types';
import { useSetLifeCycleStatus } from '../hooks/useLifeCycles';

interface LifeCycleStatusModalProps {
  article: Article;
  onClose: () => void;
  databaseId: string;
  recommendation?: string;
  isRecommendationLoading?: boolean;
}

const LifeCycleStatusModal: React.FC<LifeCycleStatusModalProps> = ({
  article,
  onClose,
  databaseId,
  recommendation,
  isRecommendationLoading,
}) => {
  const [status, setStatus] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const { setLifeCycleStatus, isLoading } = useSetLifeCycleStatus(
    databaseId,
    article.id.toString()
  );

  const statusOptions = [
    'Lancement',
    'Croissance', 
    'Maturité',
    'Déclin',
    'Arrêt'
  ];

  const mapTranslatedStatus = (recommendation: string) => {
    const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const map: Record<string, string> = {
      launch: 'Lancement',
      lancement: 'Lancement',
      start: 'Lancement',
      starting: 'Lancement',
      growth: 'Croissance',
      croissance: 'Croissance',
      growing: 'Croissance',
      maturity: 'Maturité',
      maturite: 'Maturité',
      mature: 'Maturité',
      decline: 'Déclin',
      declin: 'Déclin',
      declining: 'Déclin',
      stop: 'Arrêt',
      arret: 'Arrêt',
      'arret ': 'Arrêt',
      'arrêt': 'Arrêt',
      halt: 'Arrêt',
      end: 'Arrêt',
      ending: 'Arrêt',
      finished: 'Arrêt',
      terminated: 'Arrêt',
      undefined: 'Non défini'
    };
    return map[normalize(recommendation)] || recommendation;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (status && date) {
      setLifeCycleStatus({ status, date }, {
        onSuccess: () => {
          onClose();
        },
        onError: (error) => {
          console.error('Error updating lifecycle status:', error);
          alert('Erreur lors de la mise à jour du statut');
        }
      });
    }
  };

  const handleApplyRecommendation = () => {
    if (!recommendation) return;
    
    const rec = recommendation.trim();
    const mapped = statusOptions.includes(rec)
      ? rec
      : mapTranslatedStatus(rec) || rec;
    if (!statusOptions.includes(mapped)) {
      alert(`Statut recommandé inconnu: "${recommendation}"`);
      return;
    }
    setStatus(mapped);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 min-w-[400px]">
        <h2 className="text-xl font-semibold mb-4">
          Mettre à jour le cycle de vie
        </h2>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Article:</p>
          <p className="font-medium">{article.name} (Réf: {article.id})</p>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-1">Recommandation:</p>
          {isRecommendationLoading ? (
            <span className="text-gray-500">Chargement...</span>
          ) : recommendation ? (
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200 text-sm">
                {mapTranslatedStatus(recommendation)}
              </span>
              <button
                type="button"
                onClick={handleApplyRecommendation}
                className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Appliquer
              </button>
            </div>
          ) : (
            <span className="text-gray-500">Aucune recommandation</span>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau statut *
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            >
              <option value="">Sélectionner un statut</option>
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
              disabled={isLoading || !status || !date}
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LifeCycleStatusModal; 